import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { ReviewAnnotation, ReviewAnnotationType, ReviewCategory, SceneReviewStatus } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useTts } from "../workspace/useTts.js";
import { chunkForTtsSegments, ttsChunkMax } from "../lib/tts.js";
import type { Scene } from "../lib/types.js";
import { anchorFromSelection, buildReadModel, rangeFromOffsets, type BuiltAnchor } from "./anchor.js";
import { ReviewPanel } from "./ReviewPanel.js";
import { ComposerModal } from "./Composer.js";
import { AskScribe } from "./AskScribe.js";
import { spliceRewrite } from "./apply.js";
import { ALL_KINDS, paintAnnotations, setHighlights } from "./highlights.js";
import { STATUS_LABELS, STATUS_ORDER } from "./constants.js";

interface Props {
  projectId: string;
  chapterId: string;
  chapterTitle: string;
  reviewStatus: SceneReviewStatus;
  onStatusChange: (status: SceneReviewStatus) => void;
  onExit: () => void;
  onApplied: (scene: Scene) => void;
  onToggleView: () => void;
}

const GAP = 48;

interface Layout { pageW: number; pageH: number; viewportW: number; twoPage: boolean; totalViews: number; totalPages: number; pagesPerView: number; }

/**
 * Book / Page View for Review Mode. Renders the current chapter as paginated,
 * book-style pages (CSS multi-column), with the same non-destructive annotation
 * model and TTS follow-along as the reading view. Scenes carry data-scene-id so
 * text anchors resolve exactly as in the scroll view; page turns never mutate prose.
 */
export function ReviewBook({ projectId, chapterId, chapterTitle, reviewStatus, onStatusChange, onExit, onApplied, onToggleView }: Props) {
  const toast = useToast();
  const [scenes, setScenes] = useState<{ id: string; title: string; content: string }[]>([]);
  const [annotations, setAnnotations] = useState<ReviewAnnotation[]>([]);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [viewIdx, setViewIdx] = useState(0);
  const [fontScale, setFontScale] = useState(1);
  const [spread, setSpread] = useState(true);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [toolbar, setToolbar] = useState<{ x: number; y: number; built: BuiltAnchor; sceneId: string } | null>(null);
  const [composer, setComposer] = useState<{ type: ReviewAnnotationType; built: BuiltAnchor | null; sceneId: string | null } | null>(null);
  const [ask, setAsk] = useState<{ built: BuiltAnchor; sceneId: string } | null>(null);
  const [applying, setApplying] = useState(false);
  const [voice, setVoice] = useState("");
  const tts = useTts(projectId);

  const stageRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<HTMLDivElement>(null);

  // ---- Load chapter content + annotations ----
  const reload = useCallback(async () => {
    try {
      const [sc, an] = await Promise.all([
        api.reviewChapterScenes(projectId, chapterId),
        api.reviewAnnotations(projectId, { scope: "chapter", chapterId })
      ]);
      setScenes(sc.scenes);
      setAnnotations(an.annotations);
    } catch { /* ignore */ }
  }, [projectId, chapterId]);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => () => { for (const k of ALL_KINDS) setHighlights(k, []); }, []);

  // ---- Pagination ----
  const measure = useCallback(() => {
    const vp = viewportRef.current;
    const flow = flowRef.current;
    const host = vp?.parentElement;
    if (!vp || !flow || !host) return;
    const availW = host.clientWidth;
    const availH = host.clientHeight;
    const twoPage = spread && availW >= 820;
    const pageW = twoPage ? Math.min(Math.floor((availW - GAP) / 2) - 24, 560) : Math.min(availW - 32, 620);
    const pageH = availH;
    const pagesPerView = twoPage ? 2 : 1;
    const viewportW = twoPage ? pageW * 2 + GAP : pageW;
    flow.style.height = `${pageH}px`;
    flow.style.width = `${viewportW}px`;
    flow.style.columnWidth = `${pageW}px`;
    flow.style.columnGap = `${GAP}px`;
    const totalPages = Math.max(1, Math.round((flow.scrollWidth + GAP) / (pageW + GAP)));
    const totalViews = Math.max(1, Math.ceil(totalPages / pagesPerView));
    setLayout({ pageW, pageH, viewportW, twoPage, totalViews, totalPages, pagesPerView });
    setViewIdx((v) => Math.min(v, totalViews - 1));
  }, [spread]);

  useLayoutEffect(() => { measure(); }, [scenes, fontScale, spread, measure]);
  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  const totalViews = layout?.totalViews ?? 1;
  const next = useCallback(() => setViewIdx((v) => Math.min(v + 1, totalViews - 1)), [totalViews]);
  const prev = useCallback(() => setViewIdx((v) => Math.max(v - 1, 0)), []);
  const offset = layout ? viewIdx * layout.pagesPerView * (layout.pageW + GAP) : 0;

  // ---- Annotation painting (re-run on page turns too, since column geometry moves) ----
  useEffect(() => {
    const flow = flowRef.current;
    if (!flow) return;
    paintAnnotations(flow, annotations, { show: showAnnotations, activeId }, (id, orphaned) =>
      void updateAnnotation(id, { orphaned })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations, showAnnotations, activeId, layout, viewIdx]);

  // ---- Selection → toolbar ----
  const captureSelection = useCallback(() => {
    const flow = flowRef.current;
    const stage = stageRef.current;
    const sel = window.getSelection();
    if (!flow || !stage || !sel || sel.isCollapsed || sel.rangeCount === 0) { setToolbar(null); return; }
    const range = sel.getRangeAt(0);
    const sceneEl = (range.startContainer.parentElement?.closest("[data-scene-id]")) as HTMLElement | null;
    if (!sceneEl || !flow.contains(sceneEl)) { setToolbar(null); return; }
    const built = anchorFromSelection(sceneEl, sel);
    if (!built) { setToolbar(null); return; }
    const rect = range.getBoundingClientRect();
    const box = stage.getBoundingClientRect();
    setToolbar({ x: rect.left - box.left + rect.width / 2, y: rect.top - box.top - 8, built, sceneId: sceneEl.dataset.sceneId! });
  }, []);
  useEffect(() => {
    const onUp = () => window.setTimeout(captureSelection, 0);
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, [captureSelection]);
  const dismissToolbar = useCallback(() => { setToolbar(null); window.getSelection()?.removeAllRanges(); }, []);

  // ---- Annotation CRUD ----
  const createQuick = useCallback(async (type: "highlight" | "strike") => {
    if (!toolbar) return;
    try {
      const { annotation } = await api.createReviewAnnotation(projectId, {
        chapterId, sceneId: toolbar.sceneId, type, anchorType: "text",
        anchor: toolbar.built.anchor, selectedText: toolbar.built.selectedText
      });
      setAnnotations((prev) => [...prev, annotation]);
      dismissToolbar();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not add markup"); }
  }, [toolbar, projectId, chapterId, dismissToolbar, toast]);

  const openComposer = useCallback((type: ReviewAnnotationType) => {
    setComposer({ type, built: toolbar?.built ?? null, sceneId: toolbar?.sceneId ?? null });
    setToolbar(null);
  }, [toolbar]);

  const startAsk = useCallback(() => {
    if (!toolbar) return;
    setAsk({ built: toolbar.built, sceneId: toolbar.sceneId });
    setToolbar(null);
  }, [toolbar]);

  const acceptAsk = useCallback(async (after: string) => {
    if (!ask) return;
    const sceneEl = flowRef.current?.querySelector<HTMLElement>(`[data-scene-id="${ask.sceneId}"]`);
    if (!sceneEl) { toast.error("Couldn't locate that scene to apply the rewrite."); return; }
    const newHtml = spliceRewrite(sceneEl, ask.built.anchor, ask.built.selectedText, after);
    if (newHtml === null) { toast.error("Couldn't locate that passage to replace — it may have changed."); return; }
    setApplying(true);
    try {
      const { scene: updated } = await api.applyReviewRewrite(projectId, ask.sceneId, newHtml, "Review rewrite");
      onApplied(updated);
      setAsk(null);
      await reload();
      toast.success("Rewrite applied — saved to version history.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not apply the rewrite.");
    } finally {
      setApplying(false);
    }
  }, [ask, projectId, onApplied, reload, toast]);

  const saveComposer = useCallback(async (note: string, category: ReviewCategory | null) => {
    if (!composer) return;
    try {
      const { annotation } = await api.createReviewAnnotation(projectId, {
        chapterId, sceneId: composer.sceneId, type: composer.type, category,
        anchorType: composer.built ? "text" : "scene",
        anchor: composer.built?.anchor ?? null, selectedText: composer.built?.selectedText ?? null,
        note: note.trim() || null
      });
      setAnnotations((prev) => [...prev, annotation]);
      setComposer(null);
      window.getSelection()?.removeAllRanges();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not save note"); }
  }, [composer, projectId, chapterId, toast]);

  const updateAnnotation = useCallback(async (id: string, patch: Parameters<typeof api.updateReviewAnnotation>[2]) => {
    try {
      const { annotation } = await api.updateReviewAnnotation(projectId, id, patch);
      setAnnotations((prev) => prev.map((a) => (a.id === id ? annotation : a)));
    } catch (err) { toast.error(err instanceof Error ? err.message : "Update failed"); }
  }, [projectId, toast]);

  const deleteAnnotation = useCallback(async (id: string) => {
    try {
      await api.deleteReviewAnnotation(projectId, id);
      setAnnotations((prev) => prev.filter((a) => a.id !== id));
    } catch (err) { toast.error(err instanceof Error ? err.message : "Delete failed"); }
  }, [projectId, toast]);

  // ---- Turn to the page that holds a model offset / range ----
  const pageOfRange = useCallback((range: Range): number | null => {
    const flow = flowRef.current;
    if (!flow || !layout) return null;
    const rect = range.getBoundingClientRect();
    const flowRect = flow.getBoundingClientRect();
    if (rect.height === 0) return null;
    const relLeft = rect.left - flowRect.left + offset; // undo current translate
    const page = Math.round(relLeft / (layout.pageW + GAP));
    return Math.floor(page / layout.pagesPerView);
  }, [layout, offset]);

  // ---- Jump to an annotation ----
  const jumpTo = useCallback((ann: ReviewAnnotation) => {
    setActiveId(ann.id);
    const flow = flowRef.current;
    if (!flow || ann.anchorType !== "text" || !ann.sceneId) return;
    const sceneEl = flow.querySelector<HTMLElement>(`[data-scene-id="${ann.sceneId}"]`);
    if (!sceneEl) return;
    const model = buildReadModel(sceneEl);
    if (ann.anchor?.start === undefined || ann.anchor.end === undefined) return;
    const range = rangeFromOffsets(model, ann.anchor.start, ann.anchor.end);
    if (!range) { toast.info("That passage can't be found — the text may have changed."); return; }
    const view = pageOfRange(range);
    if (view !== null) setViewIdx(Math.max(0, Math.min(view, totalViews - 1)));
  }, [pageOfRange, totalViews, toast]);

  const textAnns = useMemo(() => annotations.filter((a) => a.anchorType === "text"), [annotations]);
  const stepAnnotation = useCallback((dir: 1 | -1) => {
    if (textAnns.length === 0) return;
    const idx = textAnns.findIndex((a) => a.id === activeId);
    const nextAnn = textAnns[(idx + dir + textAnns.length) % textAnns.length];
    if (nextAnn) jumpTo(nextAnn);
  }, [textAnns, activeId, jumpTo]);

  // ---- TTS: read the chapter aloud, follow-along + auto page advance ----
  const readChapter = useCallback(() => {
    const flow = flowRef.current;
    const text = flow ? buildReadModel(flow).text : "";
    if (!text.trim()) { toast.info("This chapter has no narratable text yet."); return; }
    void tts.play(text, voice || undefined).catch((err) => toast.error(err instanceof Error ? err.message : "Narration failed."));
  }, [tts, voice, toast]);

  useEffect(() => {
    const flow = flowRef.current;
    if (!flow) return;
    if (tts.state === "idle" || tts.nowPlaying < 0) { setHighlights("review-reading", []); return; }
    const model = buildReadModel(flow);
    const segs = chunkForTtsSegments(model.text, ttsChunkMax(!!tts.status?.available));
    const seg = segs[tts.nowPlaying];
    if (!seg) { setHighlights("review-reading", []); return; }
    const range = rangeFromOffsets(model, seg.start, seg.end);
    setHighlights("review-reading", range ? [range] : []);
    if (range) {
      const view = pageOfRange(range);
      if (view !== null && view !== viewIdx) setViewIdx(Math.max(0, Math.min(view, totalViews - 1)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tts.nowPlaying, tts.state, tts.status?.available]);

  // ---- Keyboard ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (composer) { if (e.key === "Escape") setComposer(null); return; }
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Escape") { if (toolbar) dismissToolbar(); return; }
      if (e.key === " " && (tts.state === "playing" || tts.state === "paused")) {
        e.preventDefault(); tts.state === "playing" ? tts.pause() : tts.resume(); return;
      }
      if (toolbar) {
        const k = e.key.toLowerCase();
        if (k === "h") { e.preventDefault(); void createQuick("highlight"); }
        else if (k === "s") { e.preventDefault(); void createQuick("strike"); }
        else if (k === "c") { e.preventDefault(); openComposer("comment"); }
        else if (k === "f") { e.preventDefault(); openComposer("flag"); }
        return;
      }
      if (e.key === "ArrowRight" || e.key === "PageDown") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "[") { e.preventDefault(); stepAnnotation(-1); }
      else if (e.key === "]") { e.preventDefault(); stepAnnotation(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toolbar, composer, tts, next, prev, createQuick, openComposer, dismissToolbar, stepAnnotation]);

  const unresolvedCount = annotations.filter((a) => a.status === "unresolved").length;
  const firstFolio = layout ? viewIdx * layout.pagesPerView + 1 : 1;

  return (
    <div className="review-mode">
      <header className="review-topbar">
        <div className="row gap-2" style={{ minWidth: 0 }}>
          <button className="btn btn-sm btn-ghost" onClick={onExit} title="Exit Review Mode">
            <Icon name="x" size={15} /> Exit Review
          </button>
          <span className="review-eyebrow truncate">{chapterTitle} · Book View</span>
        </div>
        <div className="spacer" />
        <div className="review-tts">
          {tts.status && tts.status.voices.length > 1 && tts.state === "idle" && (
            <select className="select select-sm" title="Narrator voice" value={voice} onChange={(e) => setVoice(e.target.value)}>
              <option value="">{tts.status.defaultVoice || "Default"}</option>
              {tts.status.voices.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          {tts.state === "idle" ? (
            <button className="btn btn-sm btn-ghost" onClick={readChapter} title="Read this chapter aloud">
              <Icon name="volume" size={15} /> Read aloud
            </button>
          ) : (
            <span className="row gap-1">
              <span className="faint" style={{ fontSize: 11 }}>{tts.progress.index}/{tts.progress.total}</span>
              {tts.state === "playing"
                ? <button className="btn btn-icon btn-ghost btn-sm" title="Pause" onClick={tts.pause}><Icon name="pause" size={15} /></button>
                : <button className="btn btn-icon btn-ghost btn-sm" title="Resume" onClick={tts.resume}><Icon name="play" size={15} /></button>}
              <button className="btn btn-icon btn-ghost btn-sm" title="Stop" onClick={tts.stop}><Icon name="stop" size={14} /></button>
            </span>
          )}
        </div>
        <span className="review-sep" />
        <button className="btn btn-icon btn-sm btn-ghost" title="Smaller text" onClick={() => setFontScale((f) => Math.max(0.8, +(f - 0.1).toFixed(2)))}>A−</button>
        <button className="btn btn-icon btn-sm btn-ghost" title="Larger text" onClick={() => setFontScale((f) => Math.min(1.6, +(f + 0.1).toFixed(2)))}>A+</button>
        <button className="btn btn-icon btn-sm btn-ghost" title={spread ? "Single page" : "Two-page spread"} onClick={() => setSpread((s) => !s)}>
          <Icon name={spread ? "file" : "book"} size={15} />
        </button>
        <button className="btn btn-sm btn-primary" onClick={onToggleView} title="Scroll reading view">
          <Icon name="file" size={15} /> Reading
        </button>
        <select className="select select-sm" value={reviewStatus} onChange={(e) => onStatusChange(e.target.value as SceneReviewStatus)} title="Scene review status">
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <button className={`btn btn-icon btn-sm ${showAnnotations ? "btn-ghost" : "tool-btn-active"}`}
          title={showAnnotations ? "Hide annotations" : "Show annotations"} onClick={() => setShowAnnotations((v) => !v)}>
          <Icon name={showAnnotations ? "edit" : "file"} size={15} />
        </button>
        <button className={`btn btn-sm ${panelOpen ? "btn-primary" : "btn-ghost"}`} onClick={() => setPanelOpen((v) => !v)}>
          <Icon name="note" size={15} /> Review{unresolvedCount > 0 ? ` (${unresolvedCount})` : ""}
        </button>
      </header>

      <div className="review-body">
        <div className="review-book-stage" ref={stageRef} style={{ fontSize: `${fontScale}em` }}>
          <div className="reader-viewport" ref={viewportRef} style={layout ? { width: layout.viewportW, height: layout.pageH } : undefined}>
            <div className="reader-track" style={{ transform: `translateX(${-offset}px)` }}>
              <div className="reader-flow review-book-flow" ref={flowRef}>
                <h1 className="review-book-chapter-title">{chapterTitle}</h1>
                {scenes.map((s, i) => (
                  <section className="review-book-scene" key={s.id}>
                    {i > 0 && <p className="scene-break">⁂</p>}
                    <div className="reader-prose" data-scene-id={s.id}
                      dangerouslySetInnerHTML={{ __html: s.content || "<p><em>(empty scene)</em></p>" }} />
                  </section>
                ))}
              </div>
            </div>
            {layout?.twoPage && <div className="reader-spine" />}
          </div>

          <button className="reader-zone reader-zone-left" onClick={prev} aria-label="Previous page" disabled={viewIdx <= 0} />
          <button className="reader-zone reader-zone-right" onClick={next} aria-label="Next page" disabled={viewIdx >= totalViews - 1} />
          <div className="reader-folio reader-folio-left">{firstFolio <= (layout?.totalPages ?? 1) ? firstFolio : ""}</div>
          {layout?.twoPage && <div className="reader-folio reader-folio-right">{firstFolio + 1 <= (layout?.totalPages ?? 1) ? firstFolio + 1 : ""}</div>}
          <div className="reader-progress">{viewIdx + 1} / {totalViews}</div>

          {toolbar && (
            <div className="review-toolbar" style={{ left: toolbar.x, top: toolbar.y }} onMouseDown={(e) => e.preventDefault()}>
              <button title="Highlight (H)" onClick={() => void createQuick("highlight")}><Icon name="edit" size={14} /></button>
              <button title="Comment (C)" onClick={() => openComposer("comment")}><Icon name="note" size={14} /></button>
              <button title="Strike (S)" onClick={() => void createQuick("strike")}><Icon name="minus" size={14} /></button>
              <button title="Flag (F)" onClick={() => openComposer("flag")}><Icon name="tag" size={14} /></button>
              <span className="review-toolbar-sep" />
              <button title="Ask Scribe" onClick={startAsk}><Icon name="sparkles" size={14} /></button>
            </div>
          )}
        </div>

        {panelOpen && (
          <ReviewPanel
            annotations={annotations}
            activeId={activeId}
            onJump={jumpTo}
            onResolve={(id, resolved) => void updateAnnotation(id, { status: resolved ? "resolved" : "unresolved" })}
            onDelete={deleteAnnotation}
            onEditNote={(id, note) => void updateAnnotation(id, { note })}
            onAddSceneNote={() => setComposer({ type: "scene_note", built: null, sceneId: scenes[0]?.id ?? null })}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>

      {composer && (
        <ComposerModal type={composer.type} selectedText={composer.built?.selectedText ?? null}
          onCancel={() => setComposer(null)} onSave={saveComposer} />
      )}

      {ask && (
        <AskScribe
          projectId={projectId}
          sceneId={ask.sceneId}
          selectedText={ask.built.selectedText}
          applying={applying}
          onCancel={() => { if (!applying) setAsk(null); }}
          onAccept={(after) => void acceptAsk(after)}
        />
      )}
    </div>
  );
}
